import { MessageType } from '../../domain';
import { ExtendedMessageType, ExtendedChannel } from '../types';
import { aggregateChannelIds } from './aggregateChannelIds';
import { filterByChannelIds } from './filterByChannelIds';
import { filterByMessageChannelIds } from './filterByMessageChannelIds';
import { filterByMessageTypeIds } from './filterByMessageTypeIds';

type ChannelOrMessageType = {
  isSubscribed: boolean;
};

// isUnsubscribed is a record of id => true where true means it is unsubscribed,
const byStatus =
  (state: boolean) =>
  ({ isSubscribed }: ChannelOrMessageType) =>
    state === isSubscribed;

export const filterByStatus = (
  status: boolean,
  channels: ExtendedChannel[],
  messageTypes: ExtendedMessageType[],
): [ExtendedChannel[], ExtendedMessageType[]] => {
  // Find matching message types + channels
  const matchingChannels = channels.filter(byStatus(status));
  const matchingMessageTypes = messageTypes.filter(byStatus(status));

  // Nothing else to do if filtering by status == subscribed
  if (status) {
    return [matchingChannels, matchingMessageTypes];
  }

  // Aggregate channels that either match *or* contain a messageType that matches
  // We don't want to hide unsubscribed message types in subscribed channels
  const matchingChannelIds = aggregateChannelIds(matchingMessageTypes, matchingChannels);

  // Aggregate message types that either match *or* are contained within a channel that matches
  // We don't want to hide message types marked as subscribed but are within unsubsctibed channels
  const containingChannelIds = aggregateChannelIds([], matchingChannels);
  const containedMessageTypes = filterByMessageChannelIds(messageTypes, containingChannelIds);

  const messageTypeIds = new Set<MessageType['id']>();
  containedMessageTypes.forEach(({ id }) => messageTypeIds.add(id));
  matchingMessageTypes.forEach(({ id }) => messageTypeIds.add(id));

  // Add channels matching by status
  return [
    filterByChannelIds(channels, matchingChannelIds),
    filterByMessageTypeIds(messageTypes, messageTypeIds),
  ];
};
